/**
 * postMessage contract between the host application and the embedded chat iframe.
 *
 * Host -> iframe messages carry auth + the target chatbot. The iframe never
 * persists the token (held in memory only) and re-reads it on `token-refresh`.
 * iframe -> host messages let the host react to lifecycle + (optionally) size
 * the modal panel to the content.
 *
 * Every message is namespaced with the `archer-chat:` prefix and validated
 * against an origin allowlist before being trusted.
 */

export const HOST_MESSAGE_PREFIX = 'archer-chat:';

/* ----------------------------- Host -> iframe ----------------------------- */

export type Theme = 'light' | 'dark';

export interface HostInitMessage {
  type: 'archer-chat:init';
  token: string;
  tenantId: string;
  chatbotId: string;
  chatbotVersionId?: string;
  locale?: string;
  theme?: Theme;
  /** Permission strings the host has resolved for the current user. */
  permissions?: string[];
  /** An existing orchestration session to resume instead of starting a new one. */
  sessionId?: string;
}

/**
 * Explicit signal that the user ended the chat (as opposed to the host merely
 * hiding/removing the iframe, e.g. when switching between parent apps). Only
 * this message tears down the backend session and clears its stored history.
 */
export interface HostEndSessionMessage {
  type: 'archer-chat:end-session';
}

export interface HostTokenRefreshMessage {
  type: 'archer-chat:token-refresh';
  token: string;
}

export interface HostThemeMessage {
  type: 'archer-chat:theme';
  theme: Theme;
}

export type HostInboundMessage =
  | HostInitMessage
  | HostTokenRefreshMessage
  | HostThemeMessage
  | HostEndSessionMessage;

/* ----------------------------- iframe -> host ----------------------------- */

export interface ReadyMessage {
  type: 'archer-chat:ready';
}

export interface InitAckMessage {
  type: 'archer-chat:init-ack';
  ok: boolean;
}

export interface ResizeMessage {
  type: 'archer-chat:resize';
  height: number;
}

export interface ErrorMessage {
  type: 'archer-chat:error';
  code: string;
  message: string;
}

export interface LifecycleMessage {
  type: 'archer-chat:event';
  event: 'session-started' | 'session-ended';
  /** The orchestration session id — present on 'session-started' so the host can store it. */
  sessionId?: string;
}

/**
 * Proactive nudge sent shortly before the current token expires. Hosts should
 * respond with a fresh `archer-chat:token-refresh`. Optional/backward-compatible:
 * a host that already pushes tokens on its own schedule can ignore this.
 */
export interface TokenExpiringMessage {
  type: 'archer-chat:token-expiring';
}

/**
 * Standard reference (deep-link) types a chatbot can point at. The host may
 * handle ANY string — these are the documented conventions so hosts and agents
 * agree on names. New types need no UI change; the host just adds a handler.
 */
export const REFERENCE_TYPES = [
  'order',
  'salesOrder',
  'purchaseOrder',
  'shipment',
  'delivery',
  'return',
  'product',
  'item',
  'inventory',
  'customer',
  'supplier',
  'invoice',
  'payment',
  'location',
  'document',
  'tracking',
  'user',
] as const;

export type ReferenceType = (typeof REFERENCE_TYPES)[number] | string;

/**
 * A structured deep-link the agent emitted inside a message. The iframe forwards
 * it to the host (which owns navigation); it never navigates itself.
 */
export interface ChatReference {
  /** Entity kind, e.g. "order". One of REFERENCE_TYPES by convention. */
  type: ReferenceType;
  /** The entity identifier. */
  id: string;
  /** Intent — defaults to "open" (e.g. open | view | edit | track). */
  action: string;
  /** Extra qualifiers parsed from the link query string. */
  params?: Record<string, string>;
  /** The human-readable text the agent used for the link. */
  label?: string;
}

export interface NavigateMessage {
  type: 'archer-chat:navigate';
  reference: ChatReference;
}

export type HostOutboundMessage =
  | ReadyMessage
  | InitAckMessage
  | ResizeMessage
  | ErrorMessage
  | LifecycleMessage
  | TokenExpiringMessage
  | NavigateMessage;

/** Runtime auth/context delivered by the host, consumed app-wide. */
export interface HostSession {
  token: string;
  tenantId: string;
  chatbotId: string;
  chatbotVersionId?: string;
  locale: string;
  theme: Theme;
  permissions: string[];
  /** An existing orchestration session to resume instead of starting a new one. */
  sessionId?: string;
}

export function isHostInboundMessage(value: unknown): value is HostInboundMessage {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  return (
    t === 'archer-chat:init' ||
    t === 'archer-chat:token-refresh' ||
    t === 'archer-chat:theme' ||
    t === 'archer-chat:end-session'
  );
}
